import { Button, Sheet, SheetContent, SheetDescription, SheetTitle } from '@invent/wl-ui-kit-next/ui';
import { X } from 'lucide-react';
import * as React from 'react';

import { FieldList, FieldSourcesHelp } from '../lists/field-picker';
import { OWN_KEY, type PlaceholderSections } from './placeholder-catalogue';

/**
 * Every template's placeholder picker (SR-523): the Lists *Columns* sheet's list over the same
 * field catalogue — one search box over names, question numbers and sources, the catalogue's
 * groups folding with their counts, each field's name and where its value comes from, a hidden
 * field counted beside the search, ↑/↓ through them — with each placeholder's sample beside its
 * name (`placeholder-catalogue.ts` builds the groups). This email's own come first and open.
 *
 * A pick puts the pill at the caret of the field last focused and closes the sheet; Shift, ⌘ or
 * Ctrl held, it stays open for the next. The sheet is not modal: a click into another field moves
 * where the next pick goes, and the sheet says where that is. A field's list at the caret opens it
 * on its search (`browse`) — "All placeholders…" or Ctrl+Space, the sheet having no button of its
 * own — and `onClosed` lets that field keep what it typed.
 */

export interface IPlaceholderSheetHandle {
  /** Opens the sheet, or keeps it open, its search `query`. */
  browse: (query: string) => void;
}

/** The sheet's search box focused, the caret after what it holds. */
function focusSearch(root: Nullable<ParentNode>) {
  const input = root?.querySelector<HTMLInputElement>('input[type="search"]');
  input?.focus();
  input?.setSelectionRange(input.value.length, input.value.length);
}

export const PlaceholderSheet = React.forwardRef<
  IPlaceholderSheetHandle,
  {
    sections: PlaceholderSections;
    /** The label of the field a pick goes into. */
    target: string;
    /** What the groups after This email hold, said under the title; null where there are none. */
    scope: Nullable<string>;
    onInsert: (name: string) => void;
    onClosed: () => void;
    /** Closed other than by a pick: the focus goes back to the field a pick would go into. */
    onReturn: () => void;
  }
>(function PlaceholderSheet({ sections, target, scope, onInsert, onClosed, onReturn }, ref) {
  const [open, setOpen] = React.useState(false);
  const [added, setAdded] = React.useState<Nullable<string>>(null);
  // The search it opens on, and a count that starts the list afresh on each.
  const [search, setSearch] = React.useState({ query: '', seq: 0 });
  const content = React.useRef<HTMLDivElement>(null);
  // A pick that closes the sheet leaves the focus in the field it went into; any other close
  // returns it to the field a pick would go into (`onReturn`).
  const inserted = React.useRef(false);
  const opening = (next: boolean, query = '') => {
    setOpen(next);
    setAdded(null);
    if (next) {
      inserted.current = false;
      setSearch((was) => ({ query, seq: was.seq + 1 }));
    }
  };

  React.useImperativeHandle(ref, () => ({ browse: (query) => opening(true, query) }));
  // Open already, the list started afresh takes the focus to its search.
  React.useEffect(() => {
    if (open) focusSearch(content.current);
  }, [search.seq]); // eslint-disable-line react-hooks/exhaustive-deps
  const closed = React.useRef(onClosed);
  closed.current = onClosed;
  React.useEffect(() => {
    if (!open) closed.current();
  }, [open]);

  const choose = (name: string, keep: boolean) => {
    const was = document.activeElement as Nullable<HTMLElement>;
    inserted.current = !keep;
    if (!keep) setOpen(false);
    onInsert(name);
    if (keep) {
      const field = sections.flatMap((section) => section.fields).find((one) => one.code === name);
      setAdded(`${field?.label ?? name} put into ${target}.`);
      // Back to the row picked, for the next.
      requestAnimationFrame(() => was?.focus());
    }
  };

  return (
    <Sheet open={open} onOpenChange={opening} modal={false}>
      <SheetContent
        ref={content}
        side="right"
        className="flex w-[min(32rem,calc(100vw-2rem))] flex-col gap-0 p-0 text-[0.8125rem]"
        onInteractOutside={(event) => event.preventDefault()}
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          focusSearch(event.currentTarget as HTMLElement);
        }}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          if (!inserted.current) onReturn();
        }}
        data-placeholder-sheet=""
      >
        <div className="grid gap-1 border-b border-border px-5 py-4">
          <div className="flex items-center gap-1">
            <SheetTitle className="text-[0.9375rem] text-[var(--color-royal-blue)]">Placeholders</SheetTitle>
            <FieldSourcesHelp>
              <p>
                <strong>This email</strong> — what the email fills itself. The fields after it are the
                participant&apos;s, as Lists shows them for this version&apos;s survey; the version for all
                surveys offers the fields every survey has, a survey&apos;s version its own as well. A
                click puts one at the caret and closes this, Shift-click or ⌘-click keeps it open, and
                typing {'{{'} or / in a field offers the same list. A value the recipient lacks sends the
                pill&apos;s fallback. Ctrl+Space in a field opens this, on what follows a {'{{'} there.
              </p>
            </FieldSourcesHelp>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="ml-auto size-8"
              aria-label="Close placeholders"
              onClick={() => opening(false)}
            >
              <X className="size-4" aria-hidden />
            </Button>
          </div>
          <SheetDescription className="not-sr-only text-[0.8125rem] text-muted-foreground" data-placeholder-target="">
            Goes into <strong className="font-medium text-foreground">{target}</strong>, at the caret.
            {scope && ` ${scope}`}
          </SheetDescription>
        </div>
        <div className="grid min-h-0 flex-1 px-3 pt-1">
          <FieldList
            key={search.seq}
            sections={sections}
            value=""
            unfolded={[OWN_KEY]}
            fill
            opening={search.query}
            onChoose={choose}
          />
        </div>
        <p role="status" className="min-h-9 border-t border-border px-5 py-2 text-xs text-muted-foreground">
          {added ?? 'Shift-click or ⌘-click puts one in and keeps this open.'}
        </p>
      </SheetContent>
    </Sheet>
  );
});
